import { Controller } from "@hotwired/stimulus"
import { PopupPicker } from "../helpers/popup_picker"

// Populates a TomSelect dropdown from the Trail/TrailStep node-or-content
// picker JSON endpoint. Options are grouped by their owning Node's name (or
// "Unassigned Content") unless the endpoint is called in node-only mode, in
// which case there's nothing to group.
//
// Optionally also drives a "+ New Content"-style popup-window flow (see
// PopupPicker for the actual popup/overlay/message-handling mechanics):
// when popupUrlValue is set, calling openPopup opens a nav-free creation
// form in a real popup window; on receipt of the new record, it's
// registered as a TomSelect option and selected.
export default class extends Controller {
  static targets = ["select"]

  static values = {
    url: String,
    selected: String,
    popupUrl: String,
    popupOptgroup: { type: String, default: "Unassigned Content" }
  }

  async connect() {
    const options = await this.loadOptions()
    this.initTomSelect(options)

    if (this.hasPopupUrlValue) {
      this.popupPicker = new PopupPicker(data => {
        this.tomselect.addOption({ value: data.id, text: data.name, optgroup: this.popupOptgroupValue })
        this.tomselect.addItem(data.id)
      })
    }
  }

  async loadOptions() {
    try {
      const response = await fetch(this.urlValue)
      return await response.json()
    } catch (error) {
      console.error("Failed to load node/content options:", error)
      return []
    }
  }

  get selectElement() {
    return this.hasSelectTarget ? this.selectTarget : this.element
  }

  initTomSelect(options) {
    const hasGroups = options.some(option => option.group)
    const settings = {
      placeholder: "Search...",
      options: options.map(option => ({ value: option.value, text: option.label, optgroup: option.group })),
      items: this.hasSelectedValue && this.selectedValue ? [this.selectedValue] : []
    }

    if (hasGroups || this.hasPopupUrlValue) {
      const groupNames = new Set(options.map(option => option.group).filter(Boolean))
      // Pre-register the popup optgroup unconditionally so a brand-new item
      // created via the popup always has a valid group to join, even if the
      // initial fetch had no unassigned entries to imply the group.
      if (this.hasPopupUrlValue) groupNames.add(this.popupOptgroupValue)

      settings.optgroupField = "optgroup"
      settings.optgroups = [...groupNames].map(name => ({ value: name, label: name }))
    }

    this.tomselect = new TomSelect(this.selectElement, settings)
  }

  openPopup(event) {
    event.preventDefault()
    if (this.popupPicker) this.popupPicker.open(this.popupUrlValue)
  }

  disconnect() {
    if (this.tomselect) {
      this.tomselect.destroy()
    }

    if (this.popupPicker) this.popupPicker.destroy()
  }
}
